import { useState } from 'react'
import { Navbar } from '../components/Navbar'
import { Footer } from '../components/Footer'
import { MeshGradient } from '../components/AnimatedBackground'
import { ArrowLeft, Mail, Lock, Loader2, AlertCircle, Eye, EyeOff } from 'lucide-react'
import { useAuth } from '../lib/auth'
import NexaticeIcon from '../assets/nexatice-icon.svg'

export function Login() {
  const { login, isLoading, error, clearError } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLocalError(null)
    clearError()

    if (!email || !password) {
      setLocalError('Please fill in all fields')
      return
    }

    const result = await login(email, password)
    if (result.success) {
      window.history.pushState({}, '', '/dashboard')
      window.dispatchEvent(new PopStateEvent('popstate'))
    }
  }

  const displayError = localError || error

  return (
    <div className="relative min-h-screen">
      <div className="noise-overlay" />
      <MeshGradient />
      <Navbar currentPage="auth" />

      <main className="relative z-10 pt-32 pb-20 px-6">
        <div className="max-w-md mx-auto">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </a>

          <div className="auth-card">
            <div className="auth-header">
              <img src={NexaticeIcon} alt="Nexatice" className="auth-logo" />
              <h1>Welcome back</h1>
              <p>Sign in to your Nexatice account</p>
            </div>

            <form onSubmit={handleSubmit} className="auth-form">
              {displayError && (
                <div className="auth-error">
                  <AlertCircle className="w-4 h-4" />
                  {displayError}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <div className="input-wrapper">
                  <Mail className="input-icon" />
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="input-wrapper">
                  <Lock className="input-icon" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="input-action"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="form-footer">
                <a href="/forgot-password" className="text-link">
                  Forgot password?
                </a>
              </div>

              <button type="submit" className="auth-submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            <div className="auth-divider">
              <span>New to Nexatice?</span>
            </div>

            <a href="/register" className="auth-alt-action">
              Create an account
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}

